import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { promptsService, OpenAIPrompt } from '@/lib/supabase/prompts';

// Define context types
interface PromptContextType {
  prompts: Record<string, OpenAIPrompt | null>;
  isLoading: boolean;
  error: Error | null;
  refreshPrompts: () => Promise<void>;
  getPromptByName: (name: string) => OpenAIPrompt | null;
  updatePrompt: (id: string, updates: Partial<OpenAIPrompt>) => Promise<OpenAIPrompt>;
}

// Create the context with default values
const PromptContext = createContext<PromptContextType>({
  prompts: {},
  isLoading: false,
  error: null,
  refreshPrompts: async () => {},
  getPromptByName: () => null,
  updatePrompt: async () => ({ id: '', name: '', promptText: '', isDefault: false, createdAt: new Date(), updatedAt: new Date() })
});

// Hook to use the prompt context
export const usePromptContext = () => useContext(PromptContext);

// Custom hook to easily access a specific prompt
export const usePrompt = (promptName: string): string | null => {
  const { prompts } = usePromptContext();
  return prompts[promptName]?.promptText || null;
};

interface PromptProviderProps {
  children: ReactNode;
}

export const PromptProvider: React.FC<PromptProviderProps> = ({ children }) => {
  const [prompts, setPrompts] = useState<Record<string, OpenAIPrompt | null>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Load all prompts on initial mount
  useEffect(() => {
    loadPrompts();
  }, []);

  // Function to load all prompts from Supabase
  const loadPrompts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get all prompts from the service
      const promptsData = await promptsService.getPrompts(true);
      
      // Convert array to object with prompt name as key
      const promptsMap: Record<string, OpenAIPrompt> = {};
      promptsData.forEach(prompt => {
        promptsMap[prompt.name] = prompt;
      });
      
      setPrompts(promptsMap);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load prompts'));
    } finally {
      setIsLoading(false);
    }
  };

  // Get a specific prompt by name
  const getPromptByName = (name: string): OpenAIPrompt | null => {
    return prompts[name] || null;
  };

  // Update a prompt and update local state
  const updatePrompt = async (id: string, updates: Partial<OpenAIPrompt>): Promise<OpenAIPrompt> => {
    try {
      // Update in Supabase
      const updatedPrompt = await promptsService.updatePrompt(id, updates);
      
      // Update local state
      setPrompts(prev => ({
        ...prev,
        [updatedPrompt.name]: updatedPrompt
      }));
      
      return updatedPrompt;
    } catch (err) {
      throw err;
    }
  };

  // Function to refresh all prompts
  const refreshPrompts = async () => {
    try {
      // Clear cache to ensure fresh data
      promptsService.clearCache();
      await loadPrompts();
    } catch (err) {
      throw err;
    }
  };

  // Context provider value
  const value: PromptContextType = {
    prompts,
    isLoading,
    error,
    refreshPrompts,
    getPromptByName,
    updatePrompt
  };

  return (
    <PromptContext.Provider value={value}>
      {children}
    </PromptContext.Provider>
  );
}; 